import { FormBlockInstance, LocalizedText } from '../api-types';

export const ITEM_SCALE_TYPE = 'item_scale';
export const ITEM_SCALE_TYPE_VERSION = 1;

/**
 * How a respondent works through the items.
 *
 * `ROWS` shows all of them at once, answered in any order. `DECK` shows one at a time in a fixed order
 * with visible progress. The difference is behaviour rather than looks, which is why pro-core keeps it
 * in `interaction`: a deck also requires every card to be answered, so it changes what finishing means.
 */
export type ItemScaleMode = 'ROWS' | 'DECK';

export interface ItemScaleItem {
  key: string;
  label: LocalizedText | null;
  description: LocalizedText | null;
}

export interface ItemScaleLevel {
  key: string;
  label: LocalizedText | null;
}

export interface ItemScaleConfig {
  items: ItemScaleItem[];
  /** One scale for every item — that is what makes the answers comparable. */
  levels: ItemScaleLevel[];
  mode: ItemScaleMode;
  requireAll: boolean;
  showItemDescriptions: boolean;
}

/** The answer shape pro-core validates: item key → level key. */
export interface ItemScaleAnswer {
  selections: Record<string, string>;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asLocalizedText = (value: unknown): LocalizedText | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const entries = Object.entries(value as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string'
  );
  return entries.length ? Object.fromEntries(entries) : null;
};

const asKeyed = <T>(
  raw: unknown,
  build: (entry: Record<string, unknown>, key: string) => T
): T[] =>
  (Array.isArray(raw) ? raw : [])
    .map(asRecord)
    .filter(entry => typeof entry.key === 'string' && entry.key.length > 0)
    .map(entry => build(entry, entry.key as string));

/**
 * Read a stored config into the shape this renderer needs.
 *
 * Tolerant for the same reason every reader here is: pro-core normalized and validated this before it
 * was stored, and a renderer that throws on a surprise takes the whole form down for the respondent.
 */
export const readItemScaleConfig = (
  block: FormBlockInstance
): ItemScaleConfig => {
  const config = asRecord(block.config);
  const data = asRecord(config.data);
  const interaction = asRecord(config.interaction);
  const presentation = asRecord(config.presentation);

  return {
    items: asKeyed(data.items, (entry, key) => ({
      key,
      label: asLocalizedText(entry.label),
      description: asLocalizedText(entry.description)
    })),
    levels: asKeyed(data.levels, (entry, key) => ({
      key,
      label: asLocalizedText(entry.label)
    })),
    mode: interaction.mode === 'DECK' ? 'DECK' : 'ROWS',
    // A deck requires every card by definition, so the flag is read as true there whatever is stored:
    // pro-core enforces it, and a renderer that let someone skip a card would walk them into a refusal.
    requireAll: interaction.mode === 'DECK' || interaction.requireAll === true,
    showItemDescriptions: presentation.showItemDescriptions === true
  };
};

export const readItemScaleAnswer = (
  answer: unknown
): ItemScaleAnswer | null => {
  const record = asRecord(answer);
  const selections = asRecord(record.selections);
  const entries = Object.entries(selections).filter(
    (entry): entry is [string, string] =>
      typeof entry[1] === 'string' && entry[1].length > 0
  );
  return { selections: Object.fromEntries(entries) };
};

/** Set one item's level. Choosing the level it already has clears it, which is how a row is undone. */
export const chooseLevel = (
  selections: Record<string, string>,
  itemKey: string,
  levelKey: string
): Record<string, string> => {
  if (selections[itemKey] !== levelKey) {
    return { ...selections, [itemKey]: levelKey };
  }
  // Choosing the level a row already has clears it, which is how a row is undone. Rebuilt by filtering
  // rather than by destructuring the key away, because the discarded binding is an unused variable the
  // linter is right to object to.
  return Object.fromEntries(
    Object.entries(selections).filter(([key]) => key !== itemKey)
  );
};

/** How many of the items carry a level the config still offers. */
export const ratedCount = (
  selections: Record<string, string>,
  config: ItemScaleConfig
): number =>
  config.items.filter(item => {
    const chosen = selections[item.key];
    return (
      chosen !== undefined && config.levels.some(level => level.key === chosen)
    );
  }).length;

/**
 * Whether an answer is complete enough to move on.
 *
 * `requireAll` is the whole rule: with it on, every item needs a level; with it off, one is enough to
 * count as having answered at all. Mirrors the server and nothing more.
 *
 * A selection naming a level the config no longer offers does not count. It cannot be rendered as
 * chosen, so counting it would leave a row that looks unanswered while Next says otherwise.
 */
export const isItemScaleAnswered = (
  answer: unknown,
  config: ItemScaleConfig
): boolean => {
  const parsed = readItemScaleAnswer(answer);
  if (!parsed || config.items.length === 0) {
    return false;
  }
  const rated = ratedCount(parsed.selections, config);
  return config.requireAll ? rated === config.items.length : rated > 0;
};
